import { FilterOption } from 'app/modules/filters/models';
import {
  getAgentFilterOption,
  getQueueFilterOption,
} from 'app/modules/filters/filters';
import { FILTER_OPERATOR, FILTER_TYPE } from 'app/modules/filters/constants';
import { QueueType } from 'app/modules/queues/models';
import { FincenCtrTablePages } from 'app/modules/fincenCtr/models';
import { CTR_FILING_STATUS_OPTIONS } from 'app/modules/fincenCtr/status';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';

// Note: These keys are tightly coupled with the BE CtrFilingFilterKey enum
// in patronus/server/filings/models.py.
export const CTR_FILING_FILTER_KEYS = {
  BSA_ID: 'bsa_id',
  CREATED_AT: 'created_at',
  CREATED_BY: 'created_by_id',
  ID: 'id',
  LAST_UPDATED_AT: 'updated_at',
  QUEUE: 'queue_id',
  REVIEWER: 'reviewer_id',
  STATUS: 'status',
  SUBMITTED: 'submitted',
} as const;

export const ALL_CTR_FILING_FILTER_OPTIONS: FilterOption[] = [
  {
    key: CTR_FILING_FILTER_KEYS.BSA_ID,
    label: 'BSA ID',
    type: FILTER_TYPE.EXACT_TEXT,
  },
  {
    key: CTR_FILING_FILTER_KEYS.CREATED_AT,
    label: 'Created at',
    type: FILTER_TYPE.DATE_RANGE,
  },
  {
    key: CTR_FILING_FILTER_KEYS.CREATED_BY,
    ...getAgentFilterOption('Created by', false),
  },
  {
    key: CTR_FILING_FILTER_KEYS.ID,
    label: 'ID',
    type: FILTER_TYPE.TEXT,
  },
  {
    key: CTR_FILING_FILTER_KEYS.QUEUE,
    ...getQueueFilterOption(QueueType.SAR_QUEUE),
  },
  {
    key: CTR_FILING_FILTER_KEYS.REVIEWER,
    ...getAgentFilterOption('Reviewer', false),
  },
  {
    key: CTR_FILING_FILTER_KEYS.STATUS,
    label: 'Status',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: { options: CTR_FILING_STATUS_OPTIONS },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: CTR_FILING_FILTER_KEYS.SUBMITTED,
    label: 'Submitted',
    type: FILTER_TYPE.BOOLEAN,
  },
  {
    key: CTR_FILING_FILTER_KEYS.LAST_UPDATED_AT,
    label: 'Updated at',
    type: FILTER_TYPE.DATE_RANGE,
  },
];

export const FILING_FILTER_OPTIONS_BY_PAGE: Record<
  FincenCtrTablePages,
  FilterOption[]
> = {
  // Reviewer is always "me" on My Filings — hide the option so the user
  // can't pick a different reviewer.
  [FincenCtrTablePages.MY_FILINGS]: ALL_CTR_FILING_FILTER_OPTIONS.filter(
    (i) => i.key !== CTR_FILING_FILTER_KEYS.REVIEWER,
  ),
  // Queue is fixed by the URL on Queue Details — no need to show the picker.
  [FincenCtrTablePages.QUEUE_DETAILS]: ALL_CTR_FILING_FILTER_OPTIONS.filter(
    (i) => i.key !== CTR_FILING_FILTER_KEYS.QUEUE,
  ),
  // BE restricts results to the agent's own queues (my_queues_only); the user
  // can still pick a queue to narrow within that set.
  [FincenCtrTablePages.QUEUED_FILINGS]: ALL_CTR_FILING_FILTER_OPTIONS,
  [FincenCtrTablePages.ADMIN]: ALL_CTR_FILING_FILTER_OPTIONS,
};

export const DEFAULT_CTR_PINNED_FILTERS_BY_PAGE: Record<
  FincenCtrTablePages,
  string[]
> = {
  [FincenCtrTablePages.MY_FILINGS]: [
    CTR_FILING_FILTER_KEYS.STATUS,
    CTR_FILING_FILTER_KEYS.CREATED_AT,
  ],
  [FincenCtrTablePages.QUEUE_DETAILS]: [
    CTR_FILING_FILTER_KEYS.STATUS,
    CTR_FILING_FILTER_KEYS.REVIEWER,
    CTR_FILING_FILTER_KEYS.CREATED_AT,
  ],
  [FincenCtrTablePages.QUEUED_FILINGS]: [
    CTR_FILING_FILTER_KEYS.STATUS,
    CTR_FILING_FILTER_KEYS.REVIEWER,
    CTR_FILING_FILTER_KEYS.CREATED_AT,
  ],
  [FincenCtrTablePages.ADMIN]: [
    CTR_FILING_FILTER_KEYS.STATUS,
    CTR_FILING_FILTER_KEYS.REVIEWER,
    CTR_FILING_FILTER_KEYS.CREATED_AT,
  ],
};

// Per-page localStorage keys so a user's pinned chips and applied filter values
// both survive navigation and page reloads.
export const PINNED_FILTERS_LOCAL_STORAGE_KEYS: Record<
  FincenCtrTablePages,
  LocalStorageKeys
> = {
  [FincenCtrTablePages.MY_FILINGS]:
    LocalStorageKeys.MY_FINCEN_CTR_PINNED_FILTERS,
  [FincenCtrTablePages.QUEUED_FILINGS]:
    LocalStorageKeys.QUEUED_FINCEN_CTR_PINNED_FILTERS,
  [FincenCtrTablePages.ADMIN]: LocalStorageKeys.ADMIN_FINCEN_CTR_PINNED_FILTERS,
  [FincenCtrTablePages.QUEUE_DETAILS]:
    LocalStorageKeys.QUEUE_DETAILS_FINCEN_CTR_PINNED_FILTERS,
};

export const FILTERS_LOCAL_STORAGE_KEYS: Record<
  FincenCtrTablePages,
  LocalStorageKeys
> = {
  [FincenCtrTablePages.MY_FILINGS]: LocalStorageKeys.MY_FINCEN_CTR_FILTERS,
  [FincenCtrTablePages.QUEUED_FILINGS]:
    LocalStorageKeys.QUEUED_FINCEN_CTR_FILTERS,
  [FincenCtrTablePages.ADMIN]: LocalStorageKeys.ADMIN_FINCEN_CTR_FILTERS,
  [FincenCtrTablePages.QUEUE_DETAILS]:
    LocalStorageKeys.QUEUE_DETAILS_FINCEN_CTR_FILTERS,
};
